import type { MachineId, MetaVerb, SetzenPayload, Snapshot } from '../shared/contract';
import type { RunDirector } from '../core/run';

/**
 * INPUT — hands to sim inputs, logged from tick 0.
 *
 * Nothing in the app may touch the sim except through here and through the UI's explicit
 * actions, and everything that goes through here is appended to the run's input log by
 * `RunDirector.push()`. That is the whole contract: **a run is (seed + input log)**. If a
 * player action changes the world without passing through this file, the replay is a lie.
 *
 * Consequences worth stating, because they are the easy things to get wrong:
 *
 * - **Aim is committed, not sampled.** The launcher angle is a value the player is
 *   *editing* (mouse position, arrow keys); the sim never sees it until Setzen fires. A
 *   run therefore records "dropped at angle 0.31", not sixty mouse positions a second.
 * - **Held keys are edge-triggered.** A held space bar drops one token, not one per frame.
 * - **Nothing here reads the clock.** The tick stamped on an input comes from the sim.
 */

export interface AimState {
  /** pachinko launcher: radians from vertical, and 0..1 power */
  angle: number;
  power: number;
  /** münzschieber / greifer: drop position across the machine */
  x: number;
  z: number;
  /** roulette: which pocket this bet backs */
  pocket: number;
  /** which token in Der Beutel is being staked */
  beutel: number;
}

/**
 * A veto on the player's hands, asked before every push.
 *
 * Die Lehrnacht is the only customer, and it is a *shell-level* gate on purpose: an
 * input this returns false for is never pushed, so it never enters the run's input log
 * and never reaches the sim. That is what lets a scripted tutorial exist without a single
 * `if (tutorial)` anywhere inside core — the sim is not being told to ignore anything,
 * it is simply not being asked.
 */
export type InputGate = (
  verb: 'setzen' | 'ruetteln' | 'glueck' | 'kassieren' | 'nachlegen' | 'meta',
  machine: MachineId,
  payload?: unknown,
) => boolean;

export interface InputOpts {
  /** the element that owns pointer aiming (the canvas) */
  target: HTMLElement;
  dir: RunDirector;
  /** the UI needs to draw the aim preview */
  onAimChange?: (a: AimState) => void;
  /** for the odds inspector: true while the inspect key/hold is down */
  onInspect?: (on: boolean) => void;
  /** hold H: the cabinet's Hilfekarte. Reference layer, never a prompt. */
  onHelp?: (on: boolean) => void;
  /** the HUD wants to know a verb fired so it can flash */
  onVerb?: (verb: string) => void;
  /**
   * A verb key was pressed and **nothing could happen** — the player is in the aisle with
   * their hands on nothing, or the run is over.
   *
   * This exists because of the most expensive bug in this project's history: at boot the
   * shell ignored every verb, and the playtest log shows twenty-eight seconds of somebody
   * pressing space, R, G, K and N against a game that had quietly decided not to listen.
   * It reads exactly like a broken build. The verb methods below all return early in that
   * case — correctly, because there is nothing to push — and the early return is precisely
   * where the silence came from. Now the silence has a door out of it.
   */
  onIgnored?: (verb: string) => void;
}

/** The phases in which the player is standing AT a cabinet, hands on it. */
const AT_CABINET: ReadonlySet<string> = new Set(['machine', 'shop', 'boss']);

export class InputMap {
  readonly aim: AimState = { angle: 0.25, power: 0.6, x: 0, z: 0, pocket: 0, beutel: 0 };

  /** set by the shell while Die Lehrnacht is running; null the rest of the time */
  gate: InputGate | null = null;

  private snap: Snapshot | null = null;
  private held = new Set<string>();
  private detach: (() => void)[] = [];
  private inspecting = false;
  private helping = false;

  constructor(private readonly o: InputOpts) {
    this.bind();
  }

  /** The loop hands us the latest frame; input decisions need to know where we are. */
  observe(s: Snapshot): void {
    this.snap = s;
  }

  dispose(): void {
    for (const d of this.detach) d();
    this.detach = [];
  }

  /**
   * The machine the player's hands are actually on.
   *
   * This asks the PHASE, not just `activeMachine`, and the difference is a real bug that
   * shipped: the sim names an active machine as soon as any verb is applied to it, which
   * is right for a replay (an input says which cabinet it is for) and wrong for a pair of
   * hands (you cannot pull a lever you are standing ten metres away from). Reading
   * `activeMachine` alone let Setzen from the aisle drop a token into pachinko without
   * paying the buy-in. A verb is only a verb when you are at the cabinet.
   */
  private get machine(): MachineId | null {
    const s = this.snap;
    if (!s) return null;
    return AT_CABINET.has(s.run.phase) ? s.run.activeMachine : null;
  }

  private get phase(): string {
    return this.snap?.run.phase ?? 'aisle';
  }

  private bind(): void {
    const key = (e: KeyboardEvent, down: boolean): void => {
      if (e.repeat && down) return;                 // held keys are edge-triggered
      const k = e.key.toLowerCase();
      if (down) {
        if (this.held.has(k)) return;
        this.held.add(k);
      } else {
        this.held.delete(k);
      }
      if (this.route(k, down, e)) e.preventDefault();
    };
    const kd = (e: KeyboardEvent): void => key(e, true);
    const ku = (e: KeyboardEvent): void => key(e, false);
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    this.detach.push(() => window.removeEventListener('keydown', kd));
    this.detach.push(() => window.removeEventListener('keyup', ku));

    // pointer aiming: editing a value, never an input to the sim on its own
    const move = (e: PointerEvent): void => {
      const r = this.o.target.getBoundingClientRect();
      const u = clamp((e.clientX - r.left) / Math.max(1, r.width), 0, 1);
      const v = clamp((e.clientY - r.top) / Math.max(1, r.height), 0, 1);
      this.aim.angle = -0.15 + u * 0.9;
      this.aim.power = clamp(1 - v, 0.15, 1);
      this.aim.x = -4 + u * 8;
      this.aim.z = -2.5 + v * 5;
      this.aim.pocket = Math.round(u * 11);
      this.o.onAimChange?.(this.aim);
    };
    const click = (e: PointerEvent): void => {
      if (e.button !== 0) return;
      this.setzen();
    };
    this.o.target.addEventListener('pointermove', move);
    this.o.target.addEventListener('pointerdown', click);
    this.detach.push(() => this.o.target.removeEventListener('pointermove', move));
    this.detach.push(() => this.o.target.removeEventListener('pointerdown', click));
  }

  private route(k: string, down: boolean, _e: KeyboardEvent): boolean {
    // the odds inspector is the one thing that cares about key-up (hold to inspect)
    if (k === 'i' || k === 'shift') {
      if (down !== this.inspecting) {
        this.inspecting = down;
        this.o.onInspect?.(down);
      }
      return true;
    }
    /**
     * DIE HILFEKARTE, on a held key like the odds inspector and for the same reason: a
     * reference card you hold up and put down is a card you consult, while one you toggle
     * is a panel you leave open and then play the game behind. The Lehrnacht never points
     * at this; it is found, or the one-time first-encounter slip mentions it once.
     */
    if (k === 'h') {
      if (down !== this.helping) {
        this.helping = down;
        this.o.onHelp?.(down);
      }
      return true;
    }
    if (!down) return false;

    switch (k) {
      case ' ':
      case 'enter':
        this.setzen();
        return true;
      case 'r':
        this.verb('ruetteln', 1);
        return true;
      case 'g':
        this.glueck();
        return true;
      case 'k':
        this.verb('kassieren', undefined);
        return true;
      case 'n':
        this.verb('nachlegen', undefined);
        return true;
      case 'escape':
        this.meta({ t: 'leave' });
        return true;
      // At the Saalchef's table the same two keys are the bats. You are not walking
      // anywhere during a boss fight, and every pinball player in the world already has
      // their fingers on the outside of the cabinet.
      case 'a':
      case 'arrowleft':
        if (this.machine === 'flipper') this.flip('left');
        else this.walk(-4);
        return true;
      case 'd':
      case 'arrowright':
        if (this.machine === 'flipper') this.flip('right');
        else this.walk(4);
        return true;
      case 'e':
        this.enterNearest();
        return true;
      case 'b':
        if (this.machine) this.meta({ t: 'brecheisen', machine: this.machine });
        return true;
      case 'f':
        if (this.machine) this.meta({ t: 'inspect', machine: this.machine });
        return true;
      case '1': case '2': case '3': case '4': case '5':
      case '6': case '7': case '8': case '9':
        this.aim.beutel = Number(k) - 1;
        this.o.onAimChange?.(this.aim);
        return true;
      default:
        return false;
    }
  }

  // ---------------------------------------------------------------- verbs

  /**
   * THE ONE DOOR. Every input in this file leaves through here, and the gate is asked
   * here, so there is exactly one place where "did this reach the sim?" is decided.
   * Returns false if the input was refused, so callers can skip their own side effects
   * (the HUD must not flash a verb that never happened).
   */
  private send(
    verb: 'setzen' | 'ruetteln' | 'glueck' | 'kassieren' | 'nachlegen' | 'meta',
    machine: MachineId,
    payload?: unknown,
  ): boolean {
    if (this.gate && !this.gate(verb, machine, payload)) return false;
    this.o.dir.push(verb, machine, payload as never);
    return true;
  }

  /** Verb 1. The aim is read here, once, and committed to the log. */
  setzen(): void {
    const m = this.machine;
    if (!m || this.phase === 'receipt' || this.phase === 'over') {
      this.o.onIgnored?.('setzen');
      return;
    }
    if (!this.send('setzen', m, this.payload(m))) return;
    this.o.onVerb?.('Setzen');
  }

  payload(m: MachineId): SetzenPayload {
    const a = this.aim;
    switch (m) {
      case 'pachinko':
        return { machine: 'pachinko', angle: a.angle, power: a.power, beutel: a.beutel };
      case 'muenzschieber':
        return { machine: 'muenzschieber', x: a.x, beutel: a.beutel };
      case 'bandit':
        return { machine: 'bandit', power: a.power, stake: 1 + (a.beutel % 3) };
      case 'roulette':
        return { machine: 'roulette', pocket: a.pocket, power: a.power, beutel: a.beutel };
      case 'greifer':
        return { machine: 'greifer', x: a.x, z: a.z, beutel: a.beutel };
      case 'flipper':
        // Setzen on the Saalchef's table is the plunger; the bats have their own keys
        // and go through `flip()` below. See the contract's SetzenPayload for why a flip
        // is a Setzen rather than a sixth verb.
        return { machine: 'flipper', flip: 'plunger', power: a.power, beutel: a.beutel };
    }
  }

  /** The bats. A/D (or the arrows) while you are at the Saalchef's table. */
  flip(side: 'left' | 'right'): void {
    const m = this.machine;
    if (m !== 'flipper') return;
    const ok = this.send('setzen', m, {
      machine: 'flipper', flip: side, power: 1, beutel: this.aim.beutel,
    });
    if (!ok) return;
    this.o.onVerb?.(side === 'left' ? 'Linker Schläger' : 'Rechter Schläger');
  }

  verb(v: 'ruetteln' | 'kassieren' | 'nachlegen', payload: unknown): void {
    const m = this.machine;
    if (!m) {
      this.o.onIgnored?.(v);
      return;
    }
    if (!this.send(v, m, payload)) return;
    this.o.onVerb?.(v);
  }

  /**
   * Verb 3. GLÜCK bends ONE DECLARED upcoming event, so the stream/event pair is part of
   * the input — "I am spending luck on the next reel stop" is the decision, and the UI's
   * picker is what chooses it. Defaulting silently would make the meter feel like a
   * slot machine rather than a tool.
   */
  glueck(stream?: string, event?: string): void {
    const m = this.machine;
    if (!m) {
      this.o.onIgnored?.('glueck');
      return;
    }
    const d = declaredFor(m);
    const ok = this.send('glueck', m, { stream: stream ?? d.stream, event: event ?? d.event });
    if (!ok) return;
    this.o.onVerb?.('GLÜCK');
  }

  meta(v: MetaVerb): void {
    const m = this.machine ?? 'pachinko';
    this.send('meta', m, v);
  }

  private walk(dx: number): void {
    const at = this.snap?.spieler[0] ?? 0;
    this.meta({ t: 'walk', x: at + dx });
  }

  /** Walking up to a cabinet and pressing E is the diorama's version of a menu. */
  private enterNearest(): void {
    const s = this.snap;
    if (!s) return;
    // Toggle off the PHASE, not off `activeMachine`. Keyed to the latter, the first E of
    // a fresh run left a cabinet the player had never entered.
    if (AT_CABINET.has(s.run.phase)) { this.meta({ t: 'leave' }); return; }
    let best: { m: MachineId; d: number } | null = null;
    for (const c of s.room) {
      if (!c.powered || c.broken) continue;
      const d = Math.abs(c.origin[0] - s.spieler[0]);
      if (!best || d < best.d) best = { m: c.machine, d };
    }
    if (best) this.meta({ t: 'enter', machine: best.m });
  }
}

/** Each machine's most useful declared event, for the one-key GLÜCK shortcut. */
export function declaredFor(m: MachineId): { stream: string; event: string; label: string } {
  switch (m) {
    case 'bandit': return { stream: 'reels', event: 'zug', label: 'nächster Walzenstopp' };
    case 'pachinko': return { stream: 'pachinko', event: 'busthole', label: 'das Bustloch' };
    case 'roulette': return { stream: 'roulette', event: 'pocket', label: 'das Fach' };
    case 'greifer': return { stream: 'greifer', event: 'wrap', label: 'der Inhalt des Päckchens' };
    // Not an odds question: a declared save IS a save (see Cabinet.consumeBend).
    case 'flipper': return { stream: 'flipper', event: 'rettung', label: 'die Ballrettung' };
    default: return { stream: 'muenzschieber', event: 'kante', label: 'die Kante' };
  }
}

const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);
